import { PropertyValues, html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { Focusable } from '../../mixins/Focusable.js';
import { Compact } from '../../mixins/Compact.js';
import { Boundary } from '../../visualizations/common/mixins/Boundary.js';
import type { Optional } from '../../types.js';
import { StyledFactory } from '../../mixins/Styled.js';
import { style } from './style.js';
import { DistributionPopout } from '../../visualizations/common/fragments/DistributionPopout.js';
import { DistributionLegend } from '../../visualizations/common/fragments/DistributionLegend.js';
import { DistributionAccessibleTable } from '../../visualizations/common/fragments/DistributionAccessibleTable.js';
import { Axis } from './fragments/Axis.js';
import { Bar } from './fragments/Bar.js';
import { Guides } from './fragments/Guides.js';
import { keyCodes } from '../../utils.js';
import { getLanguage, translationSet } from './lang.js';
import { OneUxElement } from '../../OneUxElement.js';
import { styleMap } from 'lit/directives/style-map.js';
import { classMap } from 'lit/directives/class-map.js';
import { legendItem, barItem, barChartType } from './types.js';
import palette from '../../generated/json/palette/palette.json';
import { OneUxPaletteToken } from '../../generated/design-tokens.js';
import { Lang } from '../../visualizations/common/mixins/Lang.js';
import { Implicit } from '../../mixins/Implicit.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ID_ACTIVE_BAR } from './constants.js';
import { AxisScale } from '../../visualizations/common/AxisScale.js';
import { Label } from '../../mixins/Label.js';
import { createAxisScale } from './createAxisScale.js';
import { register as _registerElement } from "../../elements/one-ux-scroll/register-element.js";
_registerElement("scroll-7edc3339a0114a5693a62290f2c4bf8e");
const Styled = StyledFactory(style);
const BaseClass = Label(Lang(Boundary(Implicit(Compact(Focusable(Styled(OneUxElement)))))));
export class OneUxBarChartElement extends BaseClass {
  static get elementType() {
    return 'one-ux-bar-chart';
  }
  @property({
    type: String
  })
  public accessor type!: barChartType;
  @property({
    type: Array
  })
  public accessor legend: legendItem[] = [];
  @property({
    type: Array
  })
  public accessor bars: barItem[] = [];
  @property({
    type: String
  })
  public accessor unit: Optional<string>;
  @property({
    type: Number
  })
  public accessor min: Optional<number>;
  @property({
    type: Number
  })
  public accessor max: Optional<number>;
  @state()
  private accessor _mouseHover = false;
  @state()
  private accessor _keyboardOpen = false;
  @state()
  private accessor _labelColumnWidth = 0;
  @state()
  private accessor _axisWidth = 0;
  @state()
  public accessor _activeBarForPopout: Optional<barItem>;
  constructor() {
    super();
    this.addEventListener('focus', () => {
      if (!this._activeBarForPopout) {
        this._activeBarForPopout = this.bars[0];
      }
    });
    this.addEventListener('blur', () => {
      this._activeBarForPopout = undefined;
      this._keyboardOpen = false;
    });
    this.addEventListener('keydown', this.#handleKeydown);
  }
  protected willUpdate(changed: PropertyValues<this>): void {
    if (changed.has('bars')) {
      this._activeBarForPopout = undefined;
    }
    if (changed.has('legend')) {
      this.legend = this.legend.map(x => ({
        ...x,
        color: palette[x.color as OneUxPaletteToken] ? `var(--one-ux-palette--${x.color})` : x.color
      }));
    }
  }
  protected updated() {
    this.#calculateLayout();
  }
  guardedRender() {
    const {
      locale,
      translations
    } = getLanguage(this._locale);
    const tickSize = 1;
    const tickSpacing = 40;
    const axisScale = createAxisScale({
      type: this.type,
      legend: this.legend,
      bars: this.bars,
      axisWidth: this._axisWidth,
      tickSpacing,
      minHardLimit: this.min,
      maxHardLimit: this.max
    });
    const hasHardMin = this.min != null;
    const hasHardMax = this.max != null;
    return html`<div
      class="one-ux-element--root"
      role="group"
      aria-roledescription="${translations.barChart}"
      aria-label=${this.label}
      tabindex="0"
      aria-activedescendant=${ifDefined(this._activeBarForPopout ? ID_ACTIVE_BAR : undefined)}
    >
      <div
        class="gridLayout"
        style=${styleMap({
      '--one-ux-bar-chart-element--label-column-width': `${this._labelColumnWidth}px`
    })}
      >
        <div class="guidesArea">
          ${this.type === 'distribution' ? nothing : Guides({
      axisScale,
      shadowRoot: this.shadowRoot!,
      width: this._axisWidth,
      tickSize,
      hasHardMin,
      hasHardMax
    })}
        </div>

        ${this.implicit ? nothing : html`<div aria-hidden="true" class="legendArea">
              ${DistributionLegend({
      metadata: this.legend,
      hideExcludedInLegend: true
    })}
            </div>`}
        ${this.#renderBars(axisScale, this._axisWidth, translations)}

        <div aria-hidden="true" class="axisArea">
          <div class="js-axis">
            ${Axis({
      type: this.type,
      locale,
      axisScale,
      width: this._axisWidth,
      tickSize,
      hasHardMin,
      hasHardMax
    })}
          </div>
        </div>
      </div>
    </div>`;
  }
  #renderBars(axisScale: AxisScale, width: number, translations: translationSet) {
    return html`<scroll-7edc3339a0114a5693a62290f2c4bf8e gutter>
      <div class="barsArea">
        ${this.bars.map((bar, barIndex) => {
      const active = this._activeBarForPopout === bar;
      const isMissingData = this.legend.every(x => bar.data[x.key] == null);
      const barIndexStyle = styleMap({
        '--one-ux-bar-chart-element--bar-index': barIndex
      });
      const legendForBar = this.legend.filter(legendItem => {
        const value = bar.data[legendItem.key];
        return value != null && (value >= 0 || this.type === 'grouped');
      });
      return html`
            ${this.compact ? nothing : html`<div
                  aria-hidden="true"
                  class=${classMap({
        labelArea: true,
        'one-ux-typography--label-300': true,
        typeDistribution: this.type === 'distribution'
      })}
                  style=${barIndexStyle}
                >
                  ${this.#getBarLabel(bar)}
                </div>`}

            <div
              aria-hidden="true"
              class=${classMap({
        'js-bar': true,
        barArea: true,
        active: active,
        inactive: !!this._activeBarForPopout && !active,
        'one-ux-typography--label-300': true
      })}
              style=${barIndexStyle}
              @click=${() => {
        this.#notifyDrilldown(bar);
      }}
              @mousemove=${() => {
        this._activeBarForPopout = bar;
        this._mouseHover = true;
      }}
              @mouseleave=${() => {
        this._activeBarForPopout = undefined;
        this._mouseHover = false;
      }}
            >
              ${this.compact ? this.#getBarLabel(bar) : nothing}
              ${Bar({
        type: this.type,
        legend: this.legend,
        bar,
        axisScale,
        width,
        implicit: this.implicit,
        translations,
        isMissingData
      })}
            </div>

            ${DistributionAccessibleTable({
        metadata: legendForBar,
        unit: this.unit,
        data: {
          name: bar.name,
          values: bar.data
        },
        locale: this._locale,
        id: active ? ID_ACTIVE_BAR : undefined
      })}
            ${active && !isMissingData && (this._keyboardOpen || this._mouseHover) ? DistributionPopout({
        name: bar.name,
        unit: this.unit,
        metadata: legendForBar,
        values: bar.data,
        referenceElement: this.shadowRoot!.querySelectorAll('.js-bar')[barIndex],
        direction: 'horizontal',
        alignment: 'center',
        hidePercentage: this.type === 'grouped'
      }) : nothing}
          `;
    })}
      </div>
    </scroll-7edc3339a0114a5693a62290f2c4bf8e>`;
  }
  #handleKeydown = (event: KeyboardEvent) => {
    const handled = () => {
      event.stopPropagation();
      event.preventDefault();
    };
    switch (event.code) {
      case keyCodes.UP:
      case keyCodes.LEFT:
        if (!this._activeBarForPopout) {
          this._activeBarForPopout = this.bars[this.bars.length - 1];
        } else {
          const previousIndex = Math.max(this.bars.indexOf(this._activeBarForPopout) - 1, 0);
          this._activeBarForPopout = this.bars[previousIndex];
        }
        handled();
        break;
      case keyCodes.DOWN:
      case keyCodes.RIGHT:
        if (!this._activeBarForPopout) {
          this._activeBarForPopout = this.bars[0];
        } else {
          const nextIndex = Math.min(this.bars.indexOf(this._activeBarForPopout) + 1, this.bars.length - 1);
          this._activeBarForPopout = this.bars[nextIndex];
        }
        handled();
        break;
      case keyCodes.HOME:
      case keyCodes.PAGEUP:
        this._activeBarForPopout = this.bars[0];
        handled();
        break;
      case keyCodes.END:
      case keyCodes.PAGEDOWN:
        this._activeBarForPopout = this.bars[this.bars.length - 1];
        handled();
        break;
      case keyCodes.RETURN:
      case keyCodes.SPACE:
        if (this._keyboardOpen) {
          this._keyboardOpen = false;
        } else if (this.#notifyDrilldown(this._activeBarForPopout!)) {
          this._keyboardOpen = true;
        }
        handled();
        break;
      case keyCodes.ESCAPE:
        this._keyboardOpen = false;
        handled();
        break;
    }
  };
  #getBarLabel(bar: barItem) {
    if (this.type === 'distribution' || this.type === 'grouped') {
      const total = this.legend.reduce((acc, legendItem) => acc + (legendItem.exclude ? 0 : bar.data[legendItem.key] || 0), 0);
      return `${bar.name} (${total})`;
    }
    return bar.name;
  }
  #calculateLayout() {
    const scrollbarWidth = this.shadowRoot?.querySelector('[one-ux-element="one-ux-scroll"]')?.shadowRoot?.querySelector<HTMLElement>('.scrollbar-y')?.clientWidth ?? 0;
    if (this.compact) {
      this._labelColumnWidth = 0;
      const padding = 2 * 4;
      this._axisWidth = Math.max(this.clientWidth - padding - scrollbarWidth, 0);
    } else {
      this._labelColumnWidth = Array.from(this.shadowRoot?.querySelectorAll('.labelArea') || []).reduce((acc, x) => Math.max(acc, x.clientWidth), 0);
      if (this._labelColumnWidth) {
        const padding = 3 * 4;
        this._axisWidth = Math.max(this.clientWidth - this._labelColumnWidth - padding - scrollbarWidth, 0);
      }
    }
  }
  #notifyDrilldown(bar: barItem) {
    return this.dispatchEvent(new CustomEvent('drilldown', {
      cancelable: true,
      detail: {
        barIndex: this.bars.indexOf(bar)
      }
    }));
  }
}